import { useTranslation } from "react-i18next";

import { Divider } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, FlexColBox } from "../../common/components";
import { spacing } from "../../styles/theme";
import { DesignPriceBrackets, ShippingTierForCurrency } from "../../types";
import { useCurrency } from "../../common/hooks/useCurrency";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import PricingService from "../../service/PricingService";
import { useAppLoading } from "../../context/AppLoading/useAppLoading";
import DistributorSection from "./components/DistributorSection";
import { usePriceCalculator } from "./common/usePriceCalculator";
import ProductSection from "./components/ProductSection";
import QuoteSection from "./components/QuoteSection";
import PricingSection from "./components/PricingSection";
import QuantitySection from "./components/QuantitySection";
import { useAuth } from "../../common/hooks/useAuth";
import { sortByShippingOption } from "../../utils/SortingUtils";
import ShippingSection from "./components/ShippingSection";

interface Props {
  open: boolean;
  onClose: () => void;
  designId: string;
  productName: string;
  defaultQuantity?: number;
}

const CalculatePriceModal = ({
  open,
  onClose,
  designId,
  productName,
  defaultQuantity,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const [pricing, setPricing] = useState<DesignPriceBrackets>();
  const [qty, setQty] = useState(defaultQuantity || pricing?.minQty || 20);
  const { getCurrency } = useCurrency();
  const containerRef = useRef<HTMLDivElement>(null);
  const { setIsAppLoading, isAppLoading } = useAppLoading();
  const [selectedShippingOption, setSelectedShippingOption] =
    useState<ShippingTierForCurrency | null>(
      pricing?.shippingOptions?.[0] ?? null
    );
  const {
    formattedCredits,
    rawUnitPrice,
    creditUnitPrice,
    notAvailableInCurrency,
    hasMinimum,
    hasReachedMaximum,
    totalPrice,
    shippingPrice,
  } = usePriceCalculator(pricing, productName, qty, selectedShippingOption);

  const { isDistributor, isAdmin } = useAuth();

  const handleRefresh = useCallback(async () => {
    const pricing = await PricingService.getDesignPricing(
      designId,
      getCurrency()
    );
    setPricing(pricing);
  }, [designId, getCurrency]);

  useEffect(() => {
    const initPricing = async () => {
      setIsAppLoading(true);
      const pricing = await PricingService.getDesignPricing(
        designId,
        getCurrency()
      );
      setPricing(pricing);
      setSelectedShippingOption(
        pricing.shippingOptions?.sort(sortByShippingOption)?.[0] ?? null
      );
      setQty(defaultQuantity || pricing?.minQty || 20);
      setIsAppLoading(false);
    };

    open && initPricing();
    // eslint-disable-next-line
  }, [designId, setIsAppLoading, open, defaultQuantity]);

  useEffect(() => {
    if (open) {
      handleRefresh();
    }
  }, [handleRefresh, open]);

  const onQtyChange = useCallback(
    (value: number | string | undefined) => {
      setQty(Number(value) || 0);
    },
    [setQty]
  );

  const canViewDistributorSection = useMemo(() => {
    return isDistributor || isAdmin;
  }, [isDistributor, isAdmin]);

  const onToggleDistributorPrices = useCallback((checked: boolean) => {
    if (checked) {
      setTimeout(() => {
        const distributorTotalPrice = document.querySelector(
          ".distributor-total-price"
        );
        if (distributorTotalPrice) {
          distributorTotalPrice.scrollIntoView({ behavior: "smooth" });
        }
      }, 200);
    }
  }, []);

  const handleChangeShippingOption = useCallback(
    (option: ShippingTierForCurrency) => {
      setSelectedShippingOption(option);
    },
    [setSelectedShippingOption]
  );

  const ShippingSectionComponent = useMemo(() => {
    return (
      <ShippingSection
        shippingPrice={shippingPrice}
        shippingOptions={pricing?.shippingOptions}
        selectedShippingOption={selectedShippingOption}
        handleChangeShippingOption={handleChangeShippingOption}
        quantity={qty}
      />
    );
  }, [
    shippingPrice,
    pricing?.shippingOptions,
    selectedShippingOption,
    handleChangeShippingOption,
    qty,
  ]);

  if (isAppLoading || !pricing) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
      }}
      title={t("pricing.calculate_price")}
      scrollToEndOnContentExpand
      content={
        <FlexColBox
          gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
          ref={containerRef}
        >
          <QuantitySection
            canViewDistributorSection={canViewDistributorSection}
            onQtyChange={onQtyChange}
            qty={qty}
            minQty={pricing.minQty}
            productName={productName}
            regularUserMaxOrderSize={
              pricing.distributor?.regularUserMaxOrderSize ?? 149
            }
            distributorMaxOrderSize={
              pricing.distributor?.distributorMaxOrderSize ?? 499
            }
          />

          {hasReachedMaximum && !isDistributor && !isAdmin && <QuoteSection />}

          {/* {hasReachedMaximum && !isDistributor && !isAdmin && (
            <TailoredMessage />
          )} */}

          <FlexColBox
            gap={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
          >
            <ProductSection
              productName={productName}
              rawUnitPrice={rawUnitPrice}
              creditUnitPrice={creditUnitPrice}
              freeCredits={pricing?.freeCredits}
              formattedCredits={formattedCredits}
              creditsUsed={pricing?.creditsUsed}
              extraCredits={creditUnitPrice.extraCredits}
            />
            <Divider />
            {ShippingSectionComponent}
            <Divider />
            {hasMinimum && !hasReachedMaximum && (
              <PricingSection
                totalPrice={totalPrice}
                shippingPrice={shippingPrice}
              />
            )}
          </FlexColBox>

          {canViewDistributorSection && (
            <DistributorSection
              pricing={pricing}
              notAvailableInCurrency={notAvailableInCurrency}
              qty={qty}
              hasReachedMaximum={hasReachedMaximum}
              hasMinimum={hasMinimum}
              onToggleDistributorPrices={onToggleDistributorPrices}
              ShippingSection={ShippingSectionComponent}
              shippingPrice={shippingPrice}
            />
          )}
        </FlexColBox>
      }
    />
  );
};

export default memo(CalculatePriceModal);
